import { NotificationStatusEnums, NotificationTypeEnums } from 'config/types/notification'
import useForceUpdate from 'hooks/useForceUpdate'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import useModal from 'hooks/useModal'
import Column from 'layout/Components/Column'
import { RowBetween, RowMiddle } from 'layout/Components/Row'
import { useCallback, useState } from 'react'
import { Trans } from 'react-i18next'
import NotificationService from 'services/NotificationService'
import { useUnreadNotificationAmount } from 'state/notification/hooks'
import styled, { css } from 'styled-components'
import { Icons } from 'svgs'
import theme from 'theme'
import colors from 'theme/ui/colors'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import IconButton from 'UIKit/Button/IconButton'
import HunnyLazyList from 'UIKit/HunnyLazyList'
import Image from 'UIKit/Image'
import Tab from 'UIKit/Tab'
import Tabs, { StyledIndicator } from 'UIKit/Tabs'
import Text from 'UIKit/Text'
import TransText from 'UIKit/Text/TransText'
import PageHeader from 'views/Profile/MobileProfile/PageHeader'
import NotificationItem, { NotificationItemLoader } from '../NotificationItem'
import ActionModal from './ActionModal'
import ViewModeInDesktop from './ViewModeControl/ViewModeInDesktop'

const LazyNotiItem = ({ item }) => <NotificationItem notification={item} />

const NotificationContent: React.FC<BoxProps> = ({ ...props }) => {
  const [notificationType, setNotificationType] = useState<NotificationTypeEnums>(NotificationTypeEnums.SYSTEM)
  const [statusType, setStatusType] = useState<NotificationStatusEnums>(NotificationStatusEnums.ALL)
  const { dependency, update: forceUpdate } = useForceUpdate()

  const { unreadPrivateAmount, unreadSystemAmount, updateUnreadPrivateAmount, updateUnreadSystemAmount } =
    useUnreadNotificationAmount()

  const [presentActionModal] = useModal(ActionModal)

  const isDisplayMarkAllAsReadButton =
    (notificationType === NotificationTypeEnums.SYSTEM && unreadSystemAmount > 0) ||
    (notificationType === NotificationTypeEnums.PRIVATE && unreadPrivateAmount > 0)

  const request = useCallback(
    (offset: number, limit: number) => {
      const response =
        notificationType === NotificationTypeEnums.PRIVATE
          ? NotificationService.getPrivateNotifications(offset, limit, statusType)
          : NotificationService.getSystemNotifications(offset, limit, statusType)

      return response
    },
    [notificationType, statusType, dependency],
  )

  const handleMarkAllAsRead = async () => {
    if (notificationType === NotificationTypeEnums.SYSTEM) {
      const systemResponse = await NotificationService.markAllAsReadSystemNotification()
      if (systemResponse.code === 'success') {
        forceUpdate()
        updateUnreadSystemAmount(0)
      }
    } else {
      const privateResponse = await NotificationService.markAllAsReadPrivateNotification()
      if (privateResponse.code === 'success') {
        forceUpdate()
        updateUnreadPrivateAmount(0)
      }
    }
  }
  const handleToggle = (value: boolean) => {
    setStatusType(value ? NotificationStatusEnums.UNREAD : NotificationStatusEnums.ALL)
  }

  const handlePresentActionModal = () => {
    presentActionModal({
      handleToggle,
      isUnread: statusType === NotificationStatusEnums.UNREAD,
      viewed: !isDisplayMarkAllAsReadButton,
      handleMarkAllAsRead,
    })
  }
  const { isMobile } = useMatchBreakpoints()

  return (
    <Column width="100%" overflowX="hidden" {...props}>
      <StyledHeader title="Notification" isBack>
        <RowMiddle justifyContent="flex-end" height="84px" pr="8px">
          <IconButton onClick={handlePresentActionModal}>
            <Icons.TripplePoint />
          </IconButton>
        </RowMiddle>
      </StyledHeader>

      <Box width="100%" pt={['0px', '', '16px']} mb="16px" zIndex={1} px="16px">
        <Box p="4px" background={colors.card_primary} borderRadius="12px">
          <Box style={{ transform: 'translateY(-2px)' }}>
            <Tabs
              value={notificationType}
              onTagChanged={(value) => {
                setNotificationType(value)
                setStatusType(NotificationStatusEnums.ALL)
              }}
              width="100%"
              height={41}
              zIndex={10}
              background={'transparent'}
              borderRadius={theme.radii.extraLarge}
              indicator={
                <StyledIndicator
                  width="100% !important"
                  height="41px !important"
                  borderRadius={theme.radii.small}
                  background="rgba(255, 255, 255, 0.08) !important"
                  position="relative"
                />
              }
            >
              <StyledTab
                value={NotificationTypeEnums.SYSTEM}
                $isActive={notificationType === NotificationTypeEnums.SYSTEM}
                minWidth="50%"
                height={41}
                position="relative"
                style={{
                  userSelect: 'none',
                }}
              >
                <Flex
                  position="absolute"
                  zIndex={10}
                  width="100%"
                  height={41}
                  alignItems="center"
                  justifyContent="center"
                  mt={'2px'}
                >
                  <TransText textAlign="center" fontSize="12px" fontWeight={700}>
                    Platform
                  </TransText>

                  <StyledUnreadAmountContainter>
                    <Text>{unreadSystemAmount}</Text>
                  </StyledUnreadAmountContainter>
                </Flex>
              </StyledTab>
              <StyledTab
                value={NotificationTypeEnums.PRIVATE}
                $isActive={notificationType === NotificationTypeEnums.PRIVATE}
                minWidth="50%"
                height={41}
                position="relative"
                style={{
                  userSelect: 'none',
                }}
              >
                <Flex
                  position="absolute"
                  zIndex={10}
                  width="100%"
                  height={41}
                  alignItems="center"
                  justifyContent="center"
                  mt={'2px'}
                >
                  <TransText textAlign="center" fontSize="12px" fontWeight={700}>
                    Personal
                  </TransText>

                  <StyledUnreadAmountContainter>
                    <Text>{unreadPrivateAmount}</Text>
                  </StyledUnreadAmountContainter>
                </Flex>
              </StyledTab>
            </Tabs>
          </Box>
        </Box>
      </Box>

      <StyledListContainer style={{ flex: 1 }} className="list">
        <Box px="16px">
          <HunnyLazyList
            uniqueKey="id"
            Item={LazyNotiItem}
            Loader={
              <>
                <NotificationItemLoader />
                <NotificationItemLoader />
                <NotificationItemLoader />
              </>
            }
            limit={10}
            request={request}
            noContent={
              <StyledNotificationEmpty flexDirection="column" alignItems="center" mt="24px">
                <Image src="/images/voucher-empty.png" height={48} width={60} />
                <Text mt="12px" fontSize="12px" color="text_disabled">
                  <Trans>No notification found</Trans>!
                </Text>
              </StyledNotificationEmpty>
            }
          />
        </Box>
      </StyledListContainer>

      {!isMobile && (
        <RowBetween
          height={['65px', '', '', '48px']}
          maxHeight={['65px', '', '', '48px']}
          px="16px"
          background={colors.card_primary}
          className="footer"
        >
          <ViewModeInDesktop isUnread={statusType === NotificationStatusEnums.UNREAD} onValueChanged={handleToggle} />

          {isDisplayMarkAllAsReadButton && (
            <RowMiddle onClick={handleMarkAllAsRead}>
              <TransText fontSize="12px" fontWeight={600} color="text_link_secondary">
                Mark all as read
              </TransText>
            </RowMiddle>
          )}
        </RowBetween>
      )}
    </Column>
  )
}

const StyledHeader = styled(PageHeader)`
  svg {
    height: 24px;
  }
`

const StyledNotificationEmpty = styled(Flex)`
  img {
    mix-blend-mode: luminosity;
    opacity: 0.6;
  }
`

const StyledListContainer = styled(Box)`
  overflow-y: auto;
  max-height: 100%;
  width: 100%;
  box-sizing: border-box;

  ::-webkit-scrollbar-thumb {
    visibility: hidden;
  }

  &:hover {
    ::-webkit-scrollbar-thumb {
      background-color: #2d3137 !important;
      visibility: visible;
    }
  }
`

const StyledUnreadAmountContainter = styled(Flex)`
  background: ${({ theme: { colors } }) => colors.text};
  align-items: center;
  justify-content: center;
  border-radius: 50px;
  min-width: 18px;
  padding-left: 6px;
  padding-right: 6px;
  margin-left: 8px;
  height: 18px;

  ${Text} {
    color: ${({ theme: { colors } }) => colors.inputPlaceHolder} !important;
    font-size: 12px;
    font-weight: bold;
  }
`

const StyledTab = styled(Tab)<{ $isActive: boolean }>`
  font-size: 14px;
  font-weight: ${({ $isActive }) => ($isActive ? 'bold' : 'normal')};
  color: ${({ $isActive, theme: { colors } }) => ($isActive ? colors.text : colors.textSubtle)};
  ${Text} {
    color: ${({ $isActive, theme: { colors } }) => ($isActive ? colors.text : colors.textSubtle)};
  }

  ${({ $isActive }) =>
    !$isActive &&
    css`
      ${StyledUnreadAmountContainter} {
        background: ${({ theme: { colors } }) => colors.backgroundAlt2} !important;
        color: ${({ theme: { colors } }) => colors.textTertiary} !important;
      }
    `}
`

export default NotificationContent
